interface ErrorResponseLike {
  status?: number
  data?: {
    message?: string
    code?: string
  }
  headers?: Record<string, string | undefined>
}

interface ErrorLike {
  response?: ErrorResponseLike
  message?: string
}

interface MapAuthErrorOptions {
  /**
   * Wording to use for HTTP 401.
   *   - `'invalid-credentials'` (default): for actions where 401 means the
   *     user typed the wrong password / OTP (login, forgot-password steps).
   *   - `'session-expired'`: for actions that require an existing session
   *     (e.g. selecting a company, creating an entity).
   */
  unauthorizedAs?: 'invalid-credentials' | 'session-expired'
}

/**
 * Map an HTTP / network error to a user-facing message tailored for auth flows.
 *
 * Order of resolution:
 *   1. No response on the error → connectivity problem.
 *   2. Status-specific copy. For 422 / 4xx where the backend usually has
 *      meaningful copy, the backend `data.message` is preferred.
 *   3. Caller-provided `contextFallback` (e.g. "Login failed").
 */
export function mapAuthError(
  error: unknown,
  contextFallback: string,
  options: MapAuthErrorOptions = {},
): string {
  const err = error as ErrorLike | undefined
  const response = err?.response

  if (!response) {
    return 'Connection issue. Please check your internet and try again.'
  }

  const status = response.status
  const backendMessage = response.data?.message

  switch (status) {
    case 401:
      return options.unauthorizedAs === 'session-expired'
        ? 'Your session has expired. Please log in again.'
        : 'Invalid credentials. Please try again.'

    case 403:
      return backendMessage || "You don't have permission to do this."

    case 422:
      return backendMessage || 'Please check the information you entered.'

    case 429: {
      const retryAfter = response.headers?.['retry-after']
      const seconds = retryAfter ? Number(retryAfter) : NaN
      if (Number.isFinite(seconds) && seconds > 0) {
        return `Too many attempts. Please try again in ${seconds} seconds.`
      }
      return 'Too many attempts. Please wait a moment and try again.'
    }

    case 500:
    case 502:
    case 504:
      return 'Something went wrong on our side. Please try again.'

    case 503:
      return 'Service is temporarily unavailable. Please try again shortly.'

    default:
      return backendMessage || contextFallback
  }
}
